import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon, cn, initialsOf } from "@sagot/ui";
import { UserBubble } from "@sagot/chat";
import type { WidgetForm } from "./widget-model";

const P = "deployKnowledge.widgetStudio.preview";
const FILL = "bg-[var(--wg-primary)] text-[var(--wg-on-primary)]";
const ROUND = "rounded-[var(--wg-radius)]";

interface PanelPreviewProps {
  form: WidgetForm;
  agentName: string;
  /** Shown when the welcome message is empty: the default the live chat greets with. */
  defaultGreeting: string;
  variant: "desktop" | "mobile";
  onClose?: () => void;
}

export function PreviewAvatar({ form, agentName }: { form: WidgetForm; agentName: string }) {
  const url = !form.appearance.use_agent_avatar ? form.appearance.avatar_url : null;
  if (url) return <img src={url} alt="" className="size-9 shrink-0 rounded-pill object-cover" />;
  return (
    <span aria-hidden="true" className="inline-flex size-9 shrink-0 items-center justify-center rounded-pill bg-ink-950 text-[10px] font-extrabold text-brand-500">
      {initialsOf(agentName)}
    </span>
  );
}

function PanelHeader({ form, agentName, variant, onClose }: PanelPreviewProps) {
  const { t } = useTranslation();
  const title = form.content.title || agentName || t(`${P}.defaultTitle`);
  return (
    <div className={cn("flex items-center gap-3 px-4 py-3.5", FILL)}>
      {variant === "mobile" ? <Icon name="chevron-left" className="size-5" /> : null}
      <PreviewAvatar form={form} agentName={agentName} />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-sm font-bold" data-testid="preview-title">
          {title}
        </div>
        {form.content.subtitle ? <div className="truncate text-[11px] font-medium">{form.content.subtitle}</div> : null}
      </div>
      {onClose ? (
        <button type="button" onClick={onClose} aria-label={t(`${P}.close`)} className="inline-flex size-7 items-center justify-center rounded-pill hover:opacity-80">
          <Icon name="chevron-down" className="size-4" />
        </button>
      ) : null}
    </div>
  );
}

function PanelMessages({ form, defaultGreeting, variant }: Pick<PanelPreviewProps, "form" | "defaultGreeting" | "variant">) {
  const { t } = useTranslation();
  const greeting = form.content.welcome_message.trim() || defaultGreeting;
  const starters = form.content.starter_questions.filter((q) => q.trim());
  return (
    // Scrollable sample conversation: focusable so keyboard users can scroll it (axe scrollable-region-focusable).
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <div role="region" aria-label={t(`${P}.conversation`)} tabIndex={0} className="flex-1 space-y-3 overflow-y-auto p-4 text-[13px] focus-visible:outline-2 focus-visible:outline-primary">
      {greeting ? (
        <div data-testid="preview-greeting" className={cn("max-w-[85%] whitespace-pre-wrap rounded-bl-xs bg-sunken px-3.5 py-2.5 leading-relaxed", ROUND)}>
          {greeting}
        </div>
      ) : null}
      {starters.length > 0 ? (
        <div className={cn("flex gap-1.5", variant === "mobile" ? "flex-col items-start" : "flex-wrap")}>
          {starters.map((q) => (
            <span key={q} className="rounded-pill border border-[var(--wg-primary)] px-2.5 py-1 text-[11px] font-semibold">
              {q}
            </span>
          ))}
        </div>
      ) : null}
      <UserBubble text={t(`${P}.sampleQuestion`)} />
      <div className={cn("max-w-[88%] rounded-bl-xs bg-sunken px-3.5 py-2.5 leading-relaxed", ROUND)}>
        {t(`${P}.sampleAnswer`)}
      </div>
      <div className="flex flex-wrap gap-1 text-[10px] text-fg-subtle">
        <span className="rounded-xs bg-surface-2 px-1.5 py-0.5">{t(`${P}.sampleSource`)}</span>
      </div>
    </div>
  );
}

function PanelComposer({ form }: { form: WidgetForm }) {
  const { t } = useTranslation();
  return (
    <div className="border-t border-line p-3">
      <div className={cn("flex items-center gap-2 border border-line-strong bg-surface px-3 py-2", ROUND)}>
        <span className="flex-1 truncate text-[12px] text-fg-subtle">{form.content.placeholder || t("chat.placeholder")}</span>
        <span aria-hidden="true" className={cn("inline-flex size-7 items-center justify-center rounded-pill", FILL)}>
          <Icon name="send" className="size-3.5" />
        </span>
      </div>
      {form.content.privacy_notice ? <div className="mt-2 text-center text-[10px] leading-snug text-fg-subtle">{form.content.privacy_notice}</div> : null}
      {form.appearance.show_branding ? (
        <div className="mt-1 text-center text-[10px] font-semibold text-fg-subtle">{t(`${P}.poweredBy`, { product: PRODUCT_NAME })}</div>
      ) : null}
    </div>
  );
}

/** The chat panel drawn with the tenant's draft theme (`--wg-*` vars set by the caller). */
export function WidgetPanelPreview(props: PanelPreviewProps) {
  return (
    <>
      <PanelHeader {...props} />
      <PanelMessages form={props.form} defaultGreeting={props.defaultGreeting} variant={props.variant} />
      <PanelComposer form={props.form} />
    </>
  );
}
